import type { BehindStory, GuideTip, ResourceItem } from "@/types/content";

export const viewingTips: GuideTip[] = [
  {
    title: "先看完整版，再回头看删减段落",
    body: "完整版本的节奏更慢，人物的犹豫被完整保留。建议第一遍不做暂停、不做笔记，先让情绪自然积累；第二遍再对照版本差异，理解哪些镜头在承担心理描写。",
    icon: "ri-film-line",
  },
  {
    title: "把注意力放在空间而不是台词上",
    body: "门的位置、窗帘的开合、镜面的反射，往往比对话更早透露人物处境。看第二遍时可以只观察背景：谁站在光里，谁被挡在门框之后。",
    icon: "ri-eye-line",
  },
  {
    title: "避开碎片化剪辑，保留连续的观影时间",
    body: "影片大量依赖段落之间的呼吸感，被切成十分钟一段会严重削弱张力。建议留出完整的两小时以上，并尽量使用安静的耳机或音响。",
    icon: "ri-time-line",
  },
  {
    title: "观影后留一点空白再讨论",
    body: "结尾的处理需要时间沉淀。与其立刻查解析，不如先写下自己最在意的两个细节，再去读影评，这样更容易分辨哪些观点真正说服你。",
    icon: "ri-quill-pen-line",
  },
];

export const behindStories: BehindStory[] = [
  {
    title: "一条可以走进去的老街",
    body: "美术团队先从旧照片、月份牌与城市地图入手，确认街道宽度与店铺类型，再决定哪些部分需要艺术加工。实景搭建强调“能用”：门要能推开，楼梯要能承重，柜台要能摆放真实道具。",
    image:
      "https://readdy.ai/api/search-image?query=Period%20film%20backlot%20recreating%201940s%20Shanghai%20street%2C%20wooden%20facades%20with%20vertical%20Chinese%20signboards%2C%20vintage%20film%20camera%20on%20tracks%2C%20crew%20silhouettes%2C%20warm%20tungsten%20lamps%20and%20haze%2C%20dusty%20atmospheric%20light%2C%20documentary%20style%20photography%2C%20no%20text&width=900&height=600&seq=sejie-guide-set-01&orientation=landscape",
  },
  {
    title: "旗袍为什么越穿越紧",
    body: "造型部门按剧情阶段设计了二十余套旗袍：早期宽松素净，后期剪裁讲究、颜色加深。腰线与领口高度的变化，配合角色处境同步推进，让服装成为不发声的旁白。",
    image:
      "https://readdy.ai/api/search-image?query=Tailoring%20atelier%20with%20vintage%20Chinese%20cheongsam%20patterns%20and%20fabric%20swatches%20on%20a%20wooden%20table%2C%20measuring%20tape%2C%20scissors%2C%20deep%20crimson%20and%20jade%20silk%2C%20warm%20lamp%20light%2C%20hands%20of%20a%20seamstress%20at%20work%2C%20shallow%20depth%20of%20field%2C%20nostalgic%20editorial%20photography%2C%20no%20text&width=900&height=600&seq=sejie-guide-costume-02&orientation=landscape",
  },
  {
    title: "光与烟共同完成的年代感",
    body: "现场大量使用烟雾与暖色灯具制造空气感，让远处街景自然虚化。这种处理既节省了细节成本，也让画面更接近老胶片留下的朦胧记忆。",
    image:
      "https://readdy.ai/api/search-image?query=Cinema%20lighting%20crew%20adjusting%20a%20large%20fresnel%20lamp%20and%20smoke%20diffuser%20on%20a%20period%20film%20set%2C%20warm%20beam%20cutting%20through%20haze%2C%20crimson%20curtain%20in%20background%2C%20behind%20the%20scenes%20documentary%20photography%2C%20rich%20shadows%2C%20cinematic%20atmosphere%2C%20no%20text&width=900&height=600&seq=sejie-guide-light-03&orientation=landscape",
  },
];

export const guideResources: ResourceItem[] = [
  {
    title: "《色戒》完整版：片长、版本与剪辑差异考据",
    desc: "先把版本口径理清楚，再开始比较长短。",
    type: "版本考据",
    to: "/detail/complete-version-length",
  },
  {
    title: "从张爱玲到银幕：完整版的叙事结构全解",
    desc: "理解改编如何处理原作中的大量留白。",
    type: "叙事解析",
    to: "/detail/novel-to-screen",
  },
  {
    title: "剧情解析：三场麻将戏里的暗流",
    desc: "牌桌上的位置、台词与镜头距离。",
    type: "剧情解析",
    to: "/detail/mahjong-table-tension",
  },
  {
    title: "声音设计：当配乐选择沉默",
    desc: "环境声如何承担情绪推进的工作。",
    type: "声音解析",
    to: "/detail/score-and-silence",
  },
  {
    title: "资料档案：当年的报纸与宣传物料",
    desc: "公开资料如何帮助我们理解时代语境。",
    type: "资料整理",
    to: "/detail/archive-newspapers",
  },
];